import { useEffect, useState } from 'react';
import { Lock } from 'lucide-react';
import { fetchPaymentMethods, paymentMethodsSentence } from '../lib/paymentMethods';

/**
 * Formas de pago que acepta el cobro. Para quien compra papel para su negocio,
 * "Stripe" no significa nada; ver los logos de SU tarjeta, de OXXO o de SPEI
 * sí. Las tarjetas salen de la propia página de pago ("Tipos de tarjeta
 * aceptados: Visa, Mastercard y American Express"); OXXO y SPEI solo se
 * muestran si el backend dice que la cuenta de Stripe los tiene activos.
 *
 * Marcas en SVG inline, simplificadas: cero peticiones y sin depender de un
 * CDN de íconos. aria-hidden en cada logo; el nombre va en el texto del grupo.
 */
export const ACCEPTED_CARDS = ['Visa', 'Mastercard', 'American Express'];

function Visa() {
    return (
        <svg viewBox="0 0 48 30" className="h-full w-auto" aria-hidden="true">
            <rect width="48" height="30" rx="3" fill="#fff" stroke="#D9D4CA" />
            <text x="24" y="20" textAnchor="middle" fontFamily="Arial, Helvetica, sans-serif" fontWeight="900" fontStyle="italic" fontSize="13" fill="#1A1F71" letterSpacing="0.5">VISA</text>
        </svg>
    );
}

function Mastercard() {
    return (
        <svg viewBox="0 0 48 30" className="h-full w-auto" aria-hidden="true">
            <rect width="48" height="30" rx="3" fill="#fff" stroke="#D9D4CA" />
            <circle cx="19.5" cy="15" r="8" fill="#EB001B" />
            <circle cx="28.5" cy="15" r="8" fill="#F79E1B" />
            <path d="M24 8.4a8 8 0 0 1 0 13.2a8 8 0 0 1 0-13.2z" fill="#FF5F00" />
        </svg>
    );
}

function Amex() {
    return (
        <svg viewBox="0 0 48 30" className="h-full w-auto" aria-hidden="true">
            <rect width="48" height="30" rx="3" fill="#2E77BC" />
            <text x="24" y="19" textAnchor="middle" fontFamily="Arial, Helvetica, sans-serif" fontWeight="900" fontSize="10" fill="#fff" letterSpacing="0.6">AMEX</text>
        </svg>
    );
}

function Oxxo() {
    return (
        <svg viewBox="0 0 48 30" className="h-full w-auto" aria-hidden="true">
            <rect width="48" height="30" rx="3" fill="#EC1D24" />
            <rect y="4" width="48" height="3" fill="#FFC20E" />
            <rect y="23" width="48" height="3" fill="#FFC20E" />
            <text x="24" y="19.5" textAnchor="middle" fontFamily="Arial, Helvetica, sans-serif" fontWeight="900" fontSize="11" fill="#fff" letterSpacing="0.4">OXXO</text>
        </svg>
    );
}

function Spei() {
    return (
        <svg viewBox="0 0 48 30" className="h-full w-auto" aria-hidden="true">
            <rect width="48" height="30" rx="3" fill="#fff" stroke="#D9D4CA" />
            <text x="24" y="19.5" textAnchor="middle" fontFamily="Arial, Helvetica, sans-serif" fontWeight="900" fontSize="11" fill="#1B3D6E" letterSpacing="0.4">SPEI</text>
        </svg>
    );
}

export function usePaymentMethods() {
    const [methods, setMethods] = useState(['card']);
    useEffect(() => {
        let alive = true;
        fetchPaymentMethods().then((m) => { if (alive) setMethods(m); });
        return () => { alive = false; };
    }, []);
    return methods;
}

export function CardLogos({ className = 'h-6', methods = ['card'] }) {
    const names = [...ACCEPTED_CARDS];
    if (methods.includes('oxxo')) names.push('OXXO');
    if (methods.includes('spei')) names.push('SPEI');
    return (
        <span className={`inline-flex flex-wrap items-center gap-1.5 ${className}`} role="img" aria-label={`Aceptamos ${names.join(', ')}`}>
            <Visa />
            <Mastercard />
            <Amex />
            {methods.includes('oxxo') && <Oxxo />}
            {methods.includes('spei') && <Spei />}
        </span>
    );
}

/** Logos que se actualizan solos según lo que esté activo en Stripe. */
export function AcceptedLogos({ className }) {
    const methods = usePaymentMethods();
    return <CardLogos className={className} methods={methods} />;
}

/**
 * compact: solo logos + "crédito o débito" (bajo un botón).
 * full: además, la línea de seguridad (carrito y checkout, junto al pago).
 */
export default function PaymentMethods({ variant = 'full', className = '' }) {
    const methods = usePaymentMethods();
    const sentence = paymentMethodsSentence(methods);
    return (
        <div className={`flex flex-col gap-2 ${className}`} data-testid="payment-methods">
            <CardLogos methods={methods} />
            <span className="text-xs" style={{ color: 'var(--ink-soft)' }}>
                Paga con <strong className="font-bold" style={{ color: 'var(--ink)' }}>{sentence}</strong>.
                {methods.length > 1 && ' Con OXXO o SPEI tu pedido se confirma en cuanto recibimos el pago.'}
            </span>
            {variant === 'full' && (
                <p className="flex items-start gap-1.5 text-[11px] leading-relaxed" style={{ color: 'var(--ink-soft)' }}>
                    <Lock className="w-3.5 h-3.5 flex-shrink-0 mt-px" strokeWidth={1.75} style={{ color: 'var(--success)' }} />
                    Pago cifrado en una página segura. Carpepel nunca ve ni guarda los datos de tu tarjeta.
                </p>
            )}
        </div>
    );
}
